import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EEntity } from '../../enums/entity.enum';
import { EProjects } from '../../enums/projects.enum';
import { Guests } from '../../models/guests/guests.model';
import { ConfigService } from '../../services/config/config.service';
import { Websocket, IHandlersEvent } from '../../classes/websocket/websocket.class';
import { EWsStatus } from '../../enums/websocket-status.enum';
import wsHandlers from '../../../checkin-app/modules/inbox/pages/chat/handlers';

@Injectable()
export class ProjectsComponent implements OnDestroy {
  private subscriber: Subscription;

  public isResponsive: boolean;
  public project: string;
  public header: { [key: string]: any; };

  constructor(public activatedRoute: ActivatedRoute, public config: ConfigService) {
    this.project = this.config.project;

    this.setHeaderConfig(this.config.screen.isLG);
    this.responsiveSubscriber();
    this.setQueryParams();
  }

  private setQueryParams() {
    this.activatedRoute.queryParams.subscribe(params => {
      this.config.return_url = params.return_url;
    });
  }

  private setHeaderConfig(isResponsive: boolean): void {
    this.header = {
      show: !((this.config[this.project].modules.RESPONSIVE as any).HIDE_HEADER) || !isResponsive
    };
  }

  private responsiveSubscriber(): void {
    Object.assign(this, {
      isResponsive: this.config.screen.isLG,
      subscriber: this.config.screen.onIsLGChanges().subscribe((isResponsive) => {
        this.isResponsive = isResponsive;
        this.setHeaderConfig(isResponsive);
      })
    });
  }

  public setWebSocket(): void {
    const {
      booking: {
        id: idBooking,
        mainOccupantId: idClient,
        resume: {
          idCompany
        },
        guests
      },
      checkin: {
        delegated: isDelegated
      },
      project
    } = this.config;

    const hasInboxRoute = Array.isArray(this.config.checkin.tabs) && this.config.checkin.tabs.some(tab => tab.route === 'inbox');

    if (project === String(EProjects.CHECKIN) && isDelegated && !hasInboxRoute) {
      return;
    }

    const { loggedInEntityId, entityId, loggedInEntity, entity } = this.getEntityParams(idClient, idCompany, guests, isDelegated, project);

    if (!this.config.ws) {
      this.config.ws = new Websocket({
        [EWsStatus.CONNECTED]: (send: IHandlersEvent) => wsHandlers[EWsStatus.CONNECTED]
          .bind(this)(send, idBooking, loggedInEntityId, entityId, loggedInEntity, entity),
        [EWsStatus.RECEIVED]: (send: IHandlersEvent, res: any) => wsHandlers[EWsStatus.RECEIVED](send, res),
        [EWsStatus.LOAD_CONVERSATION]: (send: IHandlersEvent, res: any) => wsHandlers[EWsStatus.LOAD_CONVERSATION].bind(this)(send, res),
        [EWsStatus.NEW_MESSAGE]: (send: IHandlersEvent, res: any) => wsHandlers[EWsStatus.NEW_MESSAGE].bind(this)(send, res),
        [EWsStatus.STORED]: (send: IHandlersEvent, res: any) => wsHandlers[EWsStatus.STORED].bind(this)(send, res)
      });
    }
  }

  public ngOnDestroy(): void {
    if (this.subscriber) {
      this.subscriber.unsubscribe();
    }
  }

  public getEntityParams(idClient: number, idCompany: number, guests: Guests, isDelegated: boolean, project: string) {
    const entityParams = {
      loggedInEntity: EEntity.CLIENT,
      loggedInEntityId: idClient,
      entity: EEntity.COMPANY,
      entityId: idCompany
    };

    if (project === String(EProjects.CHECKIN) && isDelegated) {
      const companionsList = Object.values(guests.list).filter(guest => guest.validated && (guest.delegated === guest.email));

      if (companionsList.length > 0) {
        const { id: idOccupant } = companionsList[0];

        return {
          ...entityParams,
          loggedInEntityId: idOccupant
        };
      }

      const { delegated } = guests.list[Object.keys(guests.list)[0]];

      return {
        loggedInEntity: EEntity.OTHER,
        loggedInEntityId: delegated,
        entity: EEntity.COMPANY,
        entityId: idCompany
      };
    }

    return entityParams;
  }
}
